import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useIsMobile, MoreToggle } from "../../../../components/ShowMore";

// Tabbed alumni gallery: pill tabs per event → animated masonry grid → lightbox.
// `events` = [{ title, description?, images: [] }]

const EASE = [0.23, 1, 0.32, 1];
const MOBILE_INITIAL = 6; // photos shown per event before "view more" (mobile)

const GalleryTabs = ({ events = [] }) => {
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [lb, setLb] = useState(null); // { index, dir } | null
  const ev = events[active] || { title: "", images: [] };
  const total = ev.images.length;
  const shownImages = isMobile && !expanded ? ev.images.slice(0, MOBILE_INITIAL) : ev.images;

  // collapse again when switching events
  useEffect(() => { setExpanded(false); }, [active]);

  const open = (index) => setLb({ index, dir: 0 });
  const close = () => setLb(null);
  const go = (delta) => setLb((s) => (s == null ? s : { index: (s.index + delta + total) % total, dir: delta }));

  useEffect(() => {
    if (lb == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line
  }, [lb, total]);

  const gridV = { hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.035 } } };
  const itemV = reduce
    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
    : { hidden: { opacity: 0, transform: "translateY(16px)" }, show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.4, ease: EASE } } };

  const slideV = {
    enter: (dir) => ({ opacity: 0, transform: reduce ? "translateX(0px)" : `translateX(${dir > 0 ? 40 : -40}px)` }),
    center: { opacity: 1, transform: "translateX(0px)" },
    exit: (dir) => ({ opacity: 0, transform: reduce ? "translateX(0px)" : `translateX(${dir > 0 ? -40 : 40}px)` }),
  };

  if (!events.length) return null;

  return (
    <div className="agl">
      {/* Tabs */}
      <div className="agl__tabs" role="tablist" aria-label="Gallery events">
        {events.map((e, i) => (
          <button
            key={e.title + i}
            type="button"
            role="tab"
            aria-selected={active === i}
            className={`agl__tab${active === i ? " is-active" : ""}`}
            onClick={() => setActive(i)}
          >
            <span className="agl__tab-label">{e.title}</span>
            <span className="agl__tab-count">{e.images.length}</span>
          </button>
        ))}
      </div>

      {/* Active event */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, transform: reduce ? "translateY(0px)" : "translateY(12px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
        >
          {ev.description && <p className="agl__desc">{ev.description}</p>}
          <motion.div className="agl__grid" variants={gridV} initial="hidden" animate="show">
            {shownImages.map((src, idx) => (
              <motion.figure className="agl__cell" variants={itemV} key={src + idx} onClick={() => open(idx)}>
                <img src={src} alt={`${ev.title} — photo ${idx + 1}`} loading="lazy" />
                <span className="agl__cell-zoom" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3M11 8v6M8 11h6" /></svg>
                </span>
              </motion.figure>
            ))}
          </motion.div>
          {isMobile && total > MOBILE_INITIAL && (
            <MoreToggle open={expanded} onClick={() => setExpanded((e) => !e)} moreLabel={`View all ${total} photos`} />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lb != null && (
          <motion.div
            className="agl__lb"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
            role="dialog" aria-modal="true" aria-label={`${ev.title} image viewer`}
          >
            <button type="button" className="agl__lb-close" onClick={close} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
            {total > 1 && (
              <button type="button" className="agl__lb-nav agl__lb-nav--prev" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
            )}
            <div className="agl__lb-stage" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="popLayout" custom={lb.dir} initial={false}>
                <motion.img
                  key={lb.index}
                  className="agl__lb-img"
                  src={ev.images[lb.index]}
                  alt={`${ev.title} — photo ${lb.index + 1}`}
                  custom={lb.dir}
                  variants={slideV}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration: 0.26, ease: EASE }}
                />
              </AnimatePresence>
            </div>
            {total > 1 && (
              <button type="button" className="agl__lb-nav agl__lb-nav--next" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            )}
            <div className="agl__lb-counter">{lb.index + 1} / {total}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryTabs;
